import { useState, useEffect } from "react";
import styles from "./MyLocations.module.scss";
import Button from "../../../components/common/button/Button";
import Input from "../../../components/common/input/Input";
import TrachIcon from "../../../assets/svgs/other/TrashIcon";
import LocationLargeIcon from "../../../assets/svgs/other/LocationLargeIcon";
import Select from "../../../components/common/select/Select";
import LocationService from "../../../api/services/locations.service";
import { toast } from "sonner";
import { useInfiniteScroll } from "../../../hooks/useInfiniteScroll";
import Loading from "../../../components/common/loading/Loading";
import EditIcon from "../../../assets/svgs/other/EditIcon";
import { useTranslation } from "react-i18next";
import TitleUpdater from "../../../components/common/title/TitleUpdater";
import { Address } from "../../../api/types/products.types";
import MapLocationPicker from "../../../components/common/map-location-picker/MapLocationPicker";

interface City {
  id: number;
  name: string;
}

interface Country {
  id: number;
  name: string;
}

interface AddressPayload {
  id: string;
  city_id: number | null;
  address: string;
  phone: string;
  zip_code: string;
  latitude?: number | null;
  longitude?: number | null;
}

const MyLocations = () => {
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [isAddingAddress, setIsAddingAddress] = useState(false);
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [countryId, setCountryId] = useState<number | null>(null);
  const [editingAddress, setEditingAddress] = useState<Address | null>(null);
  const [newAddress, setNewAddress] = useState<Partial<AddressPayload>>({
    city_id: null,
    address: "",
    phone: "",
    zip_code: "",
    latitude: null,
    longitude: null,
  });
  const [errors, setErrors] = useState<
    Partial<Record<keyof AddressPayload, string>>
  >({});
  const { t } = useTranslation("translation");

  const { data: countries } = useInfiniteScroll<Country>({
    fetchFn: async (page) => {
      const res = await LocationService.countries(page, 20);
      return res?.data?.data;
    },
    pageSize: 20,
  });

  const { data: cities } = useInfiniteScroll<City>({
    fetchFn: async (page) => {
      if (!countryId) return [];
      const res = await LocationService.cities(Number(countryId), page, 20);
      return res?.data?.data;
    },
    pageSize: 20,
    enabled: Boolean(countryId),
    deps: [countryId],
  });

  const loadAddresses = async () => {
    try {
      setIsLoading(true);
      const response = await LocationService.getAll(1, 50);
      setAddresses(response.data.data || []);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadAddresses();
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    if (isEditingAddress && editingAddress) {
      setEditingAddress((prev) => (prev ? { ...prev, [name]: value } : null));
    } else {
      setNewAddress((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSelectChange = (name: string, value: string | number) => {
    if (isEditingAddress && editingAddress) {
      setEditingAddress((prev) =>
        prev
          ? {
              ...prev,
              [name]:
                name === "city_id"
                  ? value === ""
                    ? null
                    : Number(value)
                  : value,
            }
          : null,
      );
    } else {
      setNewAddress((prev) => ({
        ...prev,
        [name]:
          name === "city_id" ? (value === "" ? null : Number(value)) : value,
      }));
    }
  };

  const handleCountryChange = (value: { id: string; name: string }) => {
    const countryId = Number(value.id);
    setCountryId(countryId);
    if (isEditingAddress && editingAddress) {
      setEditingAddress((prev) => (prev ? { ...prev, city_id: null } : null));
    } else {
      setNewAddress((prev) => ({ ...prev, city_id: null }));
    }
  };

  const validateForm = () => {
    let newErrors: Partial<Record<keyof AddressPayload, string>> = {};
    if (!newAddress?.address?.trim()) {
      newErrors.address = t("my_locations.errors.addressRequired") || "";
    }
    if (!newAddress?.zip_code?.trim()) {
      newErrors.zip_code = t("my_locations.errors.zipCodeRequired") || "";
    }
    if (newAddress?.city_id == null) {
      newErrors.city_id = t("my_locations.errors.cityRequired") || "";
    }
    if (!newAddress?.phone?.trim()) {
      newErrors.phone = t("my_locations.errors.phoneRequired") || "";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddAddress = async () => {
    if (!validateForm()) return;

    try {
      const formData = new FormData();
      formData.append("city_id", newAddress.city_id?.toString() || "");
      formData.append("address", newAddress.address || "");
      formData.append("phone", newAddress.phone || "");
      formData.append("zip_code", newAddress.zip_code || "");
      if (newAddress.latitude != null) {
        formData.append("latitude", newAddress.latitude.toString());
      }
      if (newAddress.longitude != null) {
        formData.append("longitude", newAddress.longitude.toString());
      }

      await LocationService.create(formData);

      await loadAddresses();

      setNewAddress({
        city_id: null,
        address: "",
        phone: "",
        zip_code: "",
        latitude: null,
        longitude: null,
      });
      setCountryId(null);
      setIsAddingAddress(false);
      setErrors({});
    } catch (error: any) {
      console.error(error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        t("common_error");
      toast.error(errorMessage);
    }
  };

  const handleEditAddress = (address: Address) => {
    setEditingAddress(address);
    setCountryId(address.city.country_id);
    setIsEditingAddress(true);
    setIsAddingAddress(false); // Close add form if open
  };

  const handleUpdateAddress = async () => {
    if (!editingAddress) return;

    try {
      const formData = new FormData();
      formData.append("city_id", editingAddress.city_id?.toString() || "");
      formData.append("address", editingAddress.address);
      formData.append("phone", editingAddress.phone);
      formData.append("zip_code", editingAddress.zip_code);
      if (editingAddress.latitude != null) {
        formData.append("latitude", editingAddress.latitude.toString());
      }
      if (editingAddress.longitude != null) {
        formData.append("longitude", editingAddress.longitude.toString());
      }
      await LocationService.update(Number(editingAddress.id), formData);
      await loadAddresses();
      setEditingAddress(null);
      setCountryId(null);
      setIsEditingAddress(false);
      setErrors({});
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        t("common_error");
      toast.error(errorMessage);
    }
  };

  const deleteAddress = async (id: string) => {
    try {
      await LocationService.delete(Number(id));
      setAddresses((prev) => prev.filter((addr) => addr.id !== id));
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        t("common_error");
      toast.error(errorMessage);
    }
  };

  const cancelEditAddress = () => {
    setIsEditingAddress(false);
    setEditingAddress(null);
    setCountryId(null);
    setErrors({});
  };

  const cancelAddAddress = () => {
    setIsAddingAddress(false);
    setNewAddress({
      city_id: null,
      address: "",
      phone: "",
      zip_code: "",
      latitude: null,
      longitude: null,
    });
    setCountryId(null);
    setErrors({});
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <div className={styles.my_locations}>
      <TitleUpdater title={t("my_locations.title")} />
      <div className="container">
        <div className={styles.content}>
          <div className={styles.header}>
            <h1 className={styles.title}>{t("my_locations.title")}</h1>
            {!isAddingAddress && (
              <Button
                onClick={() => setIsAddingAddress(true)}
                disabled={isLoading}
                className={styles.add_new_btn}
              >
                {t("my_locations.add_new_address")}
              </Button>
            )}
          </div>

          {isAddingAddress && (
            <div className={styles.add_address_form}>
              <div className={styles.form_header}>
                <h2 className={styles.form_title}>
                  {t("my_locations.modals.add_title")}
                </h2>
                <button
                  className={styles.close_form_btn}
                  onClick={cancelAddAddress}
                  disabled={isLoading}
                >
                  ×
                </button>
              </div>
              <div className={styles.form_body}>
                <div className={styles.country_city_row}>
                  <div className={styles.form_group}>
                    <Select
                      label={t("my_locations.form.country") || ""}
                      options={countries?.map((country) => ({
                        id: String(country.id),
                        name: country.name,
                      }))}
                      value={countryId?.toString() || ""}
                      onChange={handleCountryChange}
                    />
                  </div>
                  <div className={styles.form_group}>
                    <Select
                      label={t("my_locations.form.city") || ""}
                      options={cities?.map((city) => ({
                        id: String(city.id),
                        name: city.name,
                      }))}
                      value={newAddress.city_id?.toString() || ""}
                      onChange={(value) =>
                        handleSelectChange("city_id", value.id)
                      }
                      disabled={!countryId}
                      error={errors.city_id}
                    />
                  </div>
                </div>
                <div className={styles.form_group}>
                  <Input
                    label={t("my_locations.form.address") || ""}
                    placeholder={
                      t("my_locations.form.placeholders.address") || ""
                    }
                    type="text"
                    name="address"
                    value={newAddress.address}
                    onChange={handleInputChange}
                    disabled={isLoading}
                    error={errors.address}
                  />
                </div>
                <div className={styles.form_group}>
                  <MapLocationPicker
                    latitude={newAddress.latitude}
                    longitude={newAddress.longitude}
                    address={newAddress.address}
                    cityName={
                      newAddress.city_id
                        ? cities?.find((c) => c.id === newAddress.city_id)
                            ?.name || null
                        : null
                    }
                    countryName={
                      countryId
                        ? countries?.find((c) => c.id === countryId)?.name ||
                          null
                        : null
                    }
                    onLocationChange={(lat, lng, address) => {
                      setNewAddress((prev) => ({
                        ...prev,
                        latitude: lat,
                        longitude: lng,
                        address: address,
                      }));
                    }}
                    disabled={isLoading}
                  />
                </div>
                <div className={styles.form_group}>
                  <Input
                    phone
                    label={t("my_locations.form.phone_number") || ""}
                    type="tel"
                    name="phone"
                    value={newAddress.phone}
                    onChange={handleInputChange}
                    placeholder="(XXX) XXX-XXXX"
                    disabled={isLoading}
                    error={errors.phone}
                  />
                </div>
                <div className={styles.form_group}>
                  <Input
                    label={t("my_locations.form.zip_code") || ""}
                    placeholder={
                      t("my_locations.form.placeholders.zip_code") || ""
                    }
                    type="text"
                    name="zip_code"
                    value={newAddress.zip_code}
                    onChange={handleInputChange}
                    disabled={isLoading}
                    error={errors.zip_code}
                  />
                </div>
                <div className={styles.form_actions}>
                  <Button
                    onClick={cancelAddAddress}
                    className={styles.cancel_btn}
                    disabled={isLoading}
                  >
                    {t("cancel")}
                  </Button>
                  <Button
                    onClick={handleAddAddress}
                    loading={isLoading}
                    disabled={isLoading}
                    className={styles.submit_btn}
                  >
                    {t("my_locations.add_new_address")}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {addresses.length === 0 && !isAddingAddress ? (
            <div className={styles.empty_state}>
              <div className={styles.empty_icon}>
                <LocationLargeIcon />
              </div>
              <h2 className={styles.empty_title}>
                {t("my_locations.empty_state.title")}
              </h2>
              <p className={styles.empty_description}>
                {t("my_locations.empty_state.description")}
              </p>
            </div>
          ) : (
            <div className={styles.addresses_list}>
              {addresses.map((address) => (
                <div key={address.id} className={styles.address_card}>
                  <div className={styles.address_header}>
                    <h3 className={styles.address_title}>{address.address}</h3>
                    <div className={styles.left}>
                      <button
                        className={styles.edit_btn}
                        onClick={() => handleEditAddress(address)}
                        disabled={isLoading}
                      >
                        <EditIcon />
                      </button>
                      <button
                        className={styles.delete_btn}
                        onClick={() => deleteAddress(address.id)}
                        disabled={isLoading}
                      >
                        <TrachIcon />
                      </button>
                    </div>
                  </div>
                  <div className={styles.address_details}>
                    <p className={styles.city}>
                      <strong>{t("my_locations.address_card.city")}</strong>{" "}
                      {address.city.name}
                    </p>
                    <p>
                      <strong>{t("my_locations.address_card.phone")}</strong>{" "}
                      {address.phone}
                    </p>
                    <p>
                      <strong>{t("my_locations.address_card.address")}</strong>{" "}
                      {address.address}
                    </p>
                    <p>
                      <strong>{t("my_locations.address_card.zip_code")}</strong>{" "}
                      {address.zip_code}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {isEditingAddress && editingAddress && (
            <div className={styles.add_address_form}>
              <div className={styles.form_header}>
                <h2 className={styles.form_title}>
                  {t("my_locations.modals.edit_title")}
                </h2>
                <button
                  className={styles.close_form_btn}
                  onClick={cancelEditAddress}
                  disabled={isLoading}
                >
                  ×
                </button>
              </div>
              <div className={styles.form_body}>
                <div className={styles.country_city_row}>
                  <div className={styles.form_group}>
                    <Select
                      label={t("my_locations.form.country") || ""}
                      options={countries?.map((country) => ({
                        id: String(country.id),
                        name: country.name,
                      }))}
                      value={countryId?.toString() || ""}
                      onChange={handleCountryChange}
                    />
                  </div>
                  <div className={styles.form_group}>
                    <Select
                      label={t("my_locations.form.city") || ""}
                      options={cities?.map((city) => ({
                        id: String(city.id),
                        name: city.name,
                      }))}
                      value={editingAddress?.city_id?.toString() || ""}
                      onChange={(value) =>
                        handleSelectChange("city_id", value.id)
                      }
                      disabled={!countryId}
                      error={errors.city_id}
                    />
                  </div>
                </div>
                <div className={styles.form_group}>
                  <Input
                    label={t("my_locations.form.address") || ""}
                    placeholder={
                      t("my_locations.form.placeholders.address") || ""
                    }
                    type="text"
                    name="address"
                    value={editingAddress?.address}
                    onChange={handleInputChange}
                    disabled={isLoading}
                    error={errors.address}
                  />
                </div>
                <div className={styles.form_group}>
                  <MapLocationPicker
                    latitude={editingAddress?.latitude}
                    longitude={editingAddress?.longitude}
                    address={editingAddress?.address}
                    cityName={
                      editingAddress?.city_id
                        ? cities?.find((c) => c.id === editingAddress.city_id)
                            ?.name ||
                          editingAddress.city.name ||
                          null
                        : null
                    }
                    countryName={
                      countryId
                        ? countries?.find((c) => c.id === countryId)?.name ||
                          editingAddress?.city.Country?.name ||
                          null
                        : null
                    }
                    onLocationChange={(lat, lng, address) => {
                      setEditingAddress((prev) =>
                        prev
                          ? {
                              ...prev,
                              latitude: lat,
                              longitude: lng,
                              address: address,
                            }
                          : null,
                      );
                    }}
                    disabled={isLoading}
                  />
                </div>
                <div className={styles.form_group}>
                  <Input
                    phone
                    label={t("my_locations.form.phone_number") || ""}
                    type="tel"
                    name="phone"
                    value={editingAddress?.phone}
                    onChange={handleInputChange}
                    placeholder="(XXX) XXX-XXXX"
                    disabled={isLoading}
                    error={errors.phone}
                  />
                </div>
                <div className={styles.form_group}>
                  <Input
                    label={t("my_locations.form.zip_code") || ""}
                    placeholder={
                      t("my_locations.form.placeholders.zip_code") || ""
                    }
                    type="text"
                    name="zip_code"
                    value={editingAddress?.zip_code}
                    onChange={handleInputChange}
                    disabled={isLoading}
                    error={errors.zip_code}
                  />
                </div>
                <div className={styles.form_actions}>
                  <Button
                    onClick={cancelEditAddress}
                    className={styles.cancel_btn}
                    disabled={isLoading}
                  >
                    {t("cancel")}
                  </Button>
                  <Button
                    onClick={handleUpdateAddress}
                    loading={isLoading}
                    disabled={isLoading}
                    className={styles.submit_btn}
                  >
                    {t("my_locations.modals.edit_title")}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyLocations;
